import { useNavigate } from 'react-router-dom';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useProfileContext } from '../hooks/useProfileContext';
import BandzLogo from '../components/BandzLogo';

export default function HomePage() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { profiles, setActiveProfile } = useProfileContext();

  // Single-band accounts skip Home and go straight to the band's contextual
  // home (P2e — Day-of if a show is today, else Atlas).
  useEffect(() => {
    if (profiles.length === 1) {
      const only = profiles[0];
      setActiveProfile(only.id).then(() => {
        navigate(`/band/${only.slug || only.id}/home`);
      });
    }
  }, [profiles, setActiveProfile]);

  const openBand = (id: string) => {
    const p = profiles.find((x) => x.id === id);
    setActiveProfile(id).then(() => {
      // P2e — landing on a band is contextual: its own home decides between
      // Day-of (show today) and Atlas (see BandLandingPage).
      navigate(`/band/${p?.slug || id}/home`);
    });
  };

  const sorted = [...profiles].sort(
    (a, b) => (b.lastModified ?? 0) - (a.lastModified ?? 0),
  );

  return (
    <div className="home-page">
      <header className="home-hero">
        {/* LOGO T2 carries the "Bandz" wordmark — a separate h1 title underneath
            said it twice (user 10-10). The heading stays for screen readers only. */}
        <h1 className="visually-hidden">{t('home.title')}</h1>
        <BandzLogo className="home-logo" variant="full" />
        <p className="home-subtitle">
          {t('home.subtitle')}
        </p>
      </header>

      <section className="home-bands">
        <h2 className="home-section-label">{t('home.bands')}</h2>

        {sorted.length === 0 ? (
          <div className="card" style={{ textAlign: 'center', padding: '48px 24px' }}>
            <p style={{ color: 'var(--text-dim)', marginBottom: 16 }}>
              {t('home.noBands')}
            </p>
            <button
              className="btn btn-primary"
              onClick={() => navigate('/bands')}
            >
              {t('home.newBand')}
            </button>
          </div>
        ) : (
          <div className="profile-grid">
            {sorted.map((p) => (
              <button
                key={p.id}
                className="profile-card card home-band-card"
                data-home-band={p.id}
                onClick={() => openBand(p.id)}
              >
                <div className="profile-card-image">
                  {p.image ? (
                    <img src={p.image} alt={p.name} />
                  ) : (
                    <div className="profile-card-placeholder">🎵</div>
                  )}
                </div>
                <div className="profile-card-info">
                  <h3>{p.name}</h3>
                  <p className="profile-card-meta">
                    {t('home.modified', { date: new Date(p.lastModified ?? 0).toLocaleDateString() })}
                  </p>
                  <span className="btn btn-small btn-primary home-open-btn">
                    {t('home.open')}
                  </span>
                </div>
              </button>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
